import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { languages } from "../constants/languages";
import { applySiteLanguage, activeSiteLanguage } from "../utils/siteLanguage";
import { dropdownPlacement } from "../utils/dropdownPlacement";

const DROPDOWN_HEIGHT = 300;

// --- STYLES (No changes from before) ---

const SelectorContainer = styled.div`
  position: relative;
  z-index: 10000000;
  pointer-events: auto;
`;

const SelectorHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  height: 44px;
  padding: 0 16px;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  transition: background-color 0.3s;
  color: white;
  font-family: "Sora", sans-serif;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: auto;
  user-select: none;

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }

  &:active {
    background-color: rgba(255, 255, 255, 0.15);
  }
`;

const StyledGlobeIcon = styled.div`
  width: 18px;
  height: 18px;
  background-color: white;
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='currentColor'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1-H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.62-1.23 4.96-3.1 6.39z'/%3E%3C/svg%3E")
    no-repeat center;
  mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='currentColor'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.62-1.23 4.96-3.1 6.39z'/%3E%3C/svg%3E")
    no-repeat center;
`;

// Opens upward when the trigger sits low on screen (the phone menu pins it to
// the bottom of the sheet); see dropdownPlacement. Height is set inline.
const Dropdown = styled.div<{ $openUp: boolean }>`
  display: flex;
  flex-direction: column;
  position: absolute;
  ${({ $openUp }) => ($openUp ? "bottom: calc(100% + 5px);" : "top: calc(100% + 5px);")}
  left: 0;
  width: 250px;
  z-index: 9999999;
  overflow: hidden;
  background: rgba(20, 20, 40, 0.98);
  backdrop-filter: blur(20px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  pointer-events: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  outline: none;
  box-sizing: border-box;
  flex-shrink: 0;
  background: transparent;
  color: white;
  font-family: "Sora", sans-serif;
  font-size: 16px;
  pointer-events: auto;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const LanguageList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  flex-grow: 1;
  overflow-y: auto;
  pointer-events: auto;
`;

const LanguageListItem = styled.li`
  padding: 10px 12px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.9);
  font-family: "Sora", sans-serif;
  font-size: 15px;
  transition: background-color 0.2s;
  pointer-events: auto;
  user-select: none;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  &:active {
    background-color: rgba(255, 255, 255, 0.15);
  }
`;

interface Props {
  /**
   * Fired after a language is applied. Optional: the three navbar mounts pass
   * nothing and are unaffected. It exists so a host that only makes sense while
   * the visitor is still on English (the in-page language prompt) can take
   * itself off screen the moment they have chosen.
   */
  onSelect?: (langCode: string) => void;
}

const CustomLanguageSelector = ({ onSelect }: Props = {}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentLanguage, setCurrentLanguage] = useState("en");
  const [placement, setPlacement] = useState({ openUp: false, height: DROPDOWN_HEIGHT });
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleOpen = () => {
    // Measure on open: the same component sits in the desktop navbar (room
    // below) and at the foot of the phone menu (room only above).
    if (!isOpen && dropdownRef.current) {
      const { top, bottom } = dropdownRef.current.getBoundingClientRect();
      setPlacement(dropdownPlacement(top, bottom, window.innerHeight, DROPDOWN_HEIGHT));
    }
    setIsOpen(!isOpen);
  };

  const filteredLanguages = languages.filter((lang) =>
    lang.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleLanguageSelect = (langCode: string) => {
    setCurrentLanguage(langCode);
    setIsOpen(false);
    // One shared implementation, so this and the language dialog cannot drift.
    // It also writes the googtrans cookie, which this path previously did not:
    // the choice used to survive a hard reload only because useLanguageMode
    // re-wrote the cookie on the next mount.
    applySiteLanguage(langCode);
    onSelect?.(langCode);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Read the ACTIVE language back through the shared helper.
    //
    // This used to match /googtrans=([^/]*)\/([^/]*)/ against a cookie shaped
    // `googtrans=/en/hi` and take group 2. Group 1 captures the empty string
    // before the first slash, so group 2 is "en" — the SOURCE language. The
    // chip therefore read "EN" whatever you picked. Pick Hindi, reload, see
    // "EN", conclude it did not work, and pick again to no visible effect.
    const checkCookie = () => {
      const active = activeSiteLanguage();
      if (active) setCurrentLanguage(active);
    };
    const timer = setTimeout(checkCookie, 500);
    return () => clearTimeout(timer);
  }, []);

  // Deep-link language via a URL param: /?lang=kn | gu | bn (etc.) opens the page
  // pre-translated. Marketing shares astroaura.ai/?lang=kn and it lands in Kannada.
  // We drive the SAME hidden Google Translate <select> the manual selector uses,
  // polling until the widget has mounted its <select> (the Google script is async).
  useEffect(() => {
    const langParam = new URLSearchParams(window.location.search).get("lang");
    if (!langParam || langParam === "en") return;
    if (!languages.some((lang) => lang.code === langParam)) return;

    let tries = 0;
    const interval = setInterval(() => {
      const googleTranslateSelect = document.querySelector(
        "#google_translate_element select",
      );
      if (googleTranslateSelect) {
        clearInterval(interval);
        handleLanguageSelect(langParam);
      } else if (++tries > 40) {
        clearInterval(interval); // give up after ~10s
      }
    }, 250);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SelectorContainer ref={dropdownRef}>
      {/* --- THIS IS THE FIX --- */}
      {/* We add className="notranslate" to prevent Google from translating the button's text */}
      <SelectorHeader
        onClick={toggleOpen}
        className="notranslate"
      >
        <StyledGlobeIcon />
        <span>{currentLanguage.toUpperCase()}</span>
      </SelectorHeader>
      {isOpen && (
        <Dropdown $openUp={placement.openUp} style={{ height: placement.height }}>
          <SearchInput
            type="text"
            placeholder="Search language..."
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearchTerm(e.target.value)
            }
            autoFocus
          />
          <LanguageList>
            {filteredLanguages.map((lang) => (
              <LanguageListItem
                key={lang.code}
                onClick={() => handleLanguageSelect(lang.code)}
              >
                {lang.name}
              </LanguageListItem>
            ))}
          </LanguageList>
        </Dropdown>
      )}
    </SelectorContainer>
  );
};

export default CustomLanguageSelector;
